/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { StyleProps } from './types.js'
import { cx, mergeStyle, pickStyleProps } from './types.js'

export interface DateInputProps extends StyleProps {
  /** Initial selected date in YYYY-MM-DD format. */
  value?: string
  name?: string
  id?: string
  label?: string
  description?: string
  error?: string | boolean
  placeholder?: string
  disabled?: boolean
  required?: boolean
  withAsterisk?: boolean
  /** Start week on Monday instead of Sunday. Default: false */
  firstDayOfWeek?: 0 | 1
  class?: string
  style?: string | Record<string, string>
  [key: string]: unknown
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const CAL_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="12" height="12" rx="1.5"/><path d="M2 7h12M5.5 2v2M10.5 2v2"/></svg>'

type CalendarCell = { day: number; month: number; year: number; outside: boolean }

function buildCalendar(year: number, month: number, startSunday: boolean): CalendarCell[] {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const prevMonthDays = new Date(year, month, 0).getDate()
  const offset = startSunday ? firstDay : (firstDay === 0 ? 6 : firstDay - 1)

  const cells: CalendarCell[] = []

  const prevM = month === 0 ? 11 : month - 1
  const prevY = month === 0 ? year - 1 : year
  for (let i = offset - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, month: prevM, year: prevY, outside: true })
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, outside: false })
  }

  const nextM = month === 11 ? 0 : month + 1
  const nextY = month === 11 ? year + 1 : year
  let nextDay = 1
  while (cells.length % 7 !== 0) {
    cells.push({ day: nextDay++, month: nextM, year: nextY, outside: true })
  }

  return cells
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseIso(v?: string): Date | null {
  if (!v) return null
  const d = new Date(`${v}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatDisplay(iso: string): string {
  const d = parseIso(iso)
  if (!d) return ''
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

function DateInput({
  value: initialValue,
  name,
  id,
  label,
  description,
  error,
  placeholder = 'Pick a date',
  disabled = false,
  required = false,
  withAsterisk = false,
  firstDayOfWeek = 0,
  class: className,
  style,
  ...rest
}: DateInputProps) {
  const today = new Date()
  const initial = parseIso(initialValue)

  const [viewYear, setViewYear] = createSignal(initial ? initial.getFullYear() : today.getFullYear())
  const [viewMonth, setViewMonth] = createSignal(initial ? initial.getMonth() : today.getMonth())
  const [selected, setSelected] = createSignal(initialValue ?? '')
  const [open, setOpen] = createSignal(false)

  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const startSunday = firstDayOfWeek === 0
  const weekdays = startSunday
    ? ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
    : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate())
  const hasAsterisk = required || withAsterisk
  const hasError = !!error

  let rootEl: HTMLElement | null = null

  function prevMonth() {
    if (viewMonth() === 0) { setViewYear(viewYear() - 1); setViewMonth(11) }
    else setViewMonth(viewMonth() - 1)
  }

  function nextMonth() {
    if (viewMonth() === 11) { setViewYear(viewYear() + 1); setViewMonth(0) }
    else setViewMonth(viewMonth() + 1)
  }

  function selectDay(cell: CalendarCell) {
    if (cell.outside) {
      setViewYear(cell.year)
      setViewMonth(cell.month)
    }
    const dateStr = toIso(cell.year, cell.month, cell.day)
    setSelected(dateStr)
    setOpen(false)
    if (rootEl) {
      rootEl.dispatchEvent(
        new CustomEvent('dv-date-change', { detail: { date: dateStr }, bubbles: true }),
      )
    }
  }

  function handleBlur(e: FocusEvent) {
    const related = (e as FocusEvent & { relatedTarget: Element | null }).relatedTarget
    if (rootEl && related && rootEl.contains(related)) return
    setTimeout(() => setOpen(false), 150)
  }

  return (
    <div
      class={cx('dv-input-wrapper dv-date-input', className)}
      style={mergeStyle(styleObj, style)}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      ref={(el: Element) => { rootEl = el as HTMLElement }}
      {...ownRest}
    >
      {label && (
        <label class='dv-input-label' for={id} data-required={hasAsterisk || undefined}>
          {label}
          {hasAsterisk && (
            <span class='dv-input-asterisk' aria-hidden='true'>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {description && <p class='dv-input-description'>{description}</p>}
      {name && <input type='hidden' name={name} value={() => selected()} />}
      <div class='dv-date-input-trigger' data-opened={() => open() || undefined}>
        <button
          type='button'
          class='dv-date-input-button'
          id={id}
          disabled={disabled}
          aria-haspopup='dialog'
          aria-expanded={() => open()}
          onClick={() => { if (!disabled) setOpen(!open()) }}
          onBlur={handleBlur}
        >
          <span class='dv-date-input-value'>
            {() =>
              selected()
                ? formatDisplay(selected())
                : <span class='dv-date-input-placeholder'>{placeholder}</span>
            }
          </span>
          <span class='dv-date-input-icon' aria-hidden='true' dangerouslySetInnerHTML={{ __html: CAL_ICON }} />
        </button>
        <div
          class='dv-date-input-popup'
          data-opened={() => open() || undefined}
          role='dialog'
          aria-label='Date picker'
        >
          <div class='dv-date-calendar'>
            <div class='dv-date-calendar-header'>
              <button
                type='button'
                class='dv-date-calendar-nav'
                onClick={prevMonth}
                aria-label='Previous month'
              >
                ‹
              </button>
              <span class='dv-date-calendar-title'>
                {() => `${MONTH_NAMES[viewMonth()]} ${viewYear()}`}
              </span>
              <button
                type='button'
                class='dv-date-calendar-nav'
                onClick={nextMonth}
                aria-label='Next month'
              >
                ›
              </button>
            </div>
            <div class='dv-date-calendar-weekdays'>
              {weekdays.map((wd) => (
                <div class='dv-date-calendar-weekday'>{wd}</div>
              ))}
            </div>
            <div class='dv-date-calendar-grid'>
              {() => {
                const year = viewYear()
                const month = viewMonth()
                const sel = selected()
                const cells = buildCalendar(year, month, startSunday)
                return cells.map((cell) => {
                  const iso = toIso(cell.year, cell.month, cell.day)
                  return (
                    <button
                      type='button'
                      class='dv-date-calendar-cell'
                      data-today={iso === todayIso || undefined}
                      data-selected={iso === sel || undefined}
                      data-outside-month={cell.outside || undefined}
                      onClick={() => selectDay(cell)}
                      onBlur={handleBlur}
                      aria-label={iso}
                      aria-pressed={iso === sel}
                    >
                      {cell.day}
                    </button>
                  )
                })
              }}
            </div>
          </div>
        </div>
      </div>
      {hasError && error !== true && <p class='dv-input-error'>{error as string}</p>}
    </div>
  )
}

export default reactive(DateInput)
